import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Briefcase, Clock, Star, Bookmark, Eye, TrendingUp, Zap, Share2, Calendar, ChevronRight } from 'lucide-react';
import { CrownIcon } from './ui/icons';
import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';
import { Job } from '../lib/mockData';
import { ImageWithFallback } from './ui/ImageWithFallback';
import { ShareJobModal } from './ShareJobModal';
import { SimpleShareModal } from './SimpleShareModal';
import { toast } from 'sonner';
import { useApp } from '@/pages/providers/AppProvider';
import { normalizeMediaUrl, getAvatarUrl } from '@/utils/media';
import { normalizeTier, getTierLabel } from '../utils/tier';
import { useImageUpdate } from '../context/ImageUpdateContext';

interface JobCardProps {
  job: Job;
  onViewJob: (jobId: string) => void;
  onApply?: (jobId: string) => void;
  tierColor: string;
  tierBgColor: string;
  onSaveJob?: (jobId: string) => void;
  onLoginRequired?: () => void;
  isSaved?: boolean;
  isUserLoggedIn?: boolean;
  onViewCompany?: (companyName: string) => void;
  imageMode?: 'company' | 'job';
  allCompanyJobs?: Job[];
  isApplied?: boolean;
  showTierBadge?: boolean;
}

export const JobCardUpdated = React.memo(function JobCardUpdated({ job, onViewJob, onApply, tierColor, tierBgColor, onSaveJob, onLoginRequired, isSaved = false, isUserLoggedIn = false, onViewCompany, imageMode = 'company', allCompanyJobs = [], isApplied = false, showTierBadge = true }: JobCardProps) {
  const [isBookmarked, setIsBookmarked] = useState(isSaved);
  const [showShareModal, setShowShareModal] = useState(false);
  const { siteSettings, companies: globalCompanies = [] } = useApp();
  const { imageUpdateTrigger } = useImageUpdate();
  const badgeLabel = getTierLabel(job.tier, siteSettings?.tierConfig);

  const handleSaveJob = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isUserLoggedIn) {
      toast.error("Please login to save jobs");
      onLoginRequired?.();
      return;
    }
    if (onSaveJob) {
      onSaveJob(job.id);
      setIsBookmarked(!isBookmarked);
      toast.success(isBookmarked ? "Job removed from saved jobs" : "Job saved successfully");
    }
  };

  const handleShareJob = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowShareModal(true);
  };

  const handleApplyJob = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isApplied) return;
    if (!isUserLoggedIn) {
      toast.error("Please login to apply for jobs");
      onLoginRequired?.();
      return;
    }
    if (onApply) {
      try {
        await Promise.resolve(onApply(job.id));
        toast.success("Application submitted successfully!");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to apply for job";
        toast.error(message);
      }
    } else {
      // Fallback to viewing job details if no apply handler
      onViewJob(job.id);
    }
  };





  const getTierInfo = () => {
    switch (job.tier) {
      case 'megajob':
        return {
          label: badgeLabel,
          icon: Zap,
          bg: 'bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50',
          border: 'border-yellow-300/50',
          glow: 'shadow-yellow-200/50'
        };
      case 'premium':
        return {
          label: badgeLabel,
          icon: Star,
          bg: 'bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50',
          border: 'border-blue-300/50',
          glow: 'shadow-blue-200/50'
        };
      case 'prime':
        return {
          label: badgeLabel,
          icon: CrownIcon as any,
          bg: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50',
          border: 'border-green-300/50',
          glow: 'shadow-green-200/50'
        };
      case 'newspaper':
        return {
          label: badgeLabel,
          icon: Briefcase,
          bg: 'bg-gradient-to-br from-orange-50 via-red-50 to-pink-50',
          border: 'border-orange-300/50',
          glow: 'shadow-orange-200/50'
        };
      default:
        // Treat "latest" and unknown tiers as no special badge
        return {
          label: null,
          icon: Clock,
          bg: 'transparent',
          border: 'border-transparent',
          glow: ''
        };
    }
  };



  const tierInfo = getTierInfo();
  const TierIcon = tierInfo.icon;

  // Get company job count from the extended job object
  const companyJobCount = (job as any).companyJobCount || 1;

  // Resolve job/company image with priority aligned to Home page
  const getCompanyLogo = (jobData: Job): string => {
    const normalizeName = (val: string) => {
      if (!val) return '';
      // remove common suffix tokens and punctuation, collapse whitespace
      const cleaned = val
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\b(pvt|ltd|limited|inc|corp|company|co|llc|plc)\b/g, '')
        .replace(/[-_\s]+/g, ' ')
        .trim();
      return cleaned;
    };

    const nameFromJob = typeof (jobData as any).company === 'string'
      ? (jobData as any).company
      : (jobData as any).company?.name;
    const idFromJob = String((jobData as any).companyId || (jobData as any).company_id || '').trim();

    const normalizedTarget = normalizeName(String(nameFromJob || ''));

    // 0) If we have companyId, match by ID first for reliability
    if (idFromJob) {
      const base = `/uploads/company-logos/${idFromJob}`;
      const norm = normalizeMediaUrl(base);
      if (norm || base) return norm || base;
    }

    // 1) Prefer global company cover image (matches Home page visuals)
    if (normalizedTarget && Array.isArray(globalCompanies) && globalCompanies.length > 0) {
      const found = (globalCompanies as any[]).find(c => {
        const nm = normalizeName(String(c.name || c.companyName || ''));
        return nm === normalizedTarget;
      });
      if (found && (found.coverImageUrl || found.cover_image_url)) {
        const v = found.coverImageUrl || found.cover_image_url;
        return normalizeMediaUrl(v) || v;
      }
    }

    // 2) Fallback to any logo/profile image from matching global company
    if (normalizedTarget && Array.isArray(globalCompanies) && globalCompanies.length > 0) {
      const found = (globalCompanies as any[]).find(c => {
        const nm = normalizeName(String(c.name || c.companyName || ''));
        return nm === normalizedTarget;
      });
      const logoCandidate = found && (
        (found.logo_url) || (found.logoUrl) || (found.logo) ||
        (found.company_logo) || (found.companyLogo) ||
        (found.profileImage)
      );
      if (logoCandidate) {
        const v = logoCandidate as string;
        return normalizeMediaUrl(v) || v;
      }
    }

    // 3) Then explicit logo fields on the job
    if ((jobData as any).company_logo) { const v = (jobData as any).company_logo; return normalizeMediaUrl(v) || v; }
    if ((jobData as any).companyLogo) { const v = (jobData as any).companyLogo; return normalizeMediaUrl(v) || v; }
    if ((jobData as any).logo) { const v = (jobData as any).logo; return normalizeMediaUrl(v) || v; }

    // 4) Then job-specific cover image if present
    if ((jobData as any).coverImageUrl) { const v = (jobData as any).coverImageUrl; return normalizeMediaUrl(v) || v; }
    if ((jobData as any).cover_image_url) { const v = (jobData as any).cover_image_url; return normalizeMediaUrl(v) || v; }

    // 5) Placeholder based on company name
    if (nameFromJob) {
      return getAvatarUrl(String(nameFromJob), 128);
    }

    // Final fallback
    return 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=128&h=128&fit=crop&crop=face';
  };

  const getJobCover = (jobData: Job): string => {
    const v1 = (jobData as any).cover_image_url;
    const v2 = (jobData as any).coverImageUrl;
    const v3 = (jobData as any).coverImage;
    const v = v1 || v2 || v3;
    if (v) return normalizeMediaUrl(v) || v;
    const logoCandidate = (jobData as any).company_logo || (jobData as any).companyLogo || (jobData as any).logo;
    if (logoCandidate) return normalizeMediaUrl(logoCandidate) || logoCandidate as string;
    const nameFromJob = typeof (jobData as any).company === 'string' ? (jobData as any).company : (jobData as any).company?.name;
    if (nameFromJob) return getAvatarUrl(String(nameFromJob), 128);
    return 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=128&h=128&fit=crop&crop=face';
  };

  const getExperienceYearsLabel = (exp: string) => {
    const val = String(exp || '').toLowerCase();
    if (!val) return '';
    if (val.includes('entry')) return 'Less than 1 year';
    if (val.includes('junior')) return '1+ years';
    if (val.includes('mid')) return 'More than 2 years';
    if (val.includes('senior')) return 'More than 5 years';
    if (val.includes('executive')) return 'More than 7 years';
    const m = val.match(/(\d+)\s*\+?\s*year/);
    if (m) return `${m[1]}+ years`;
    return '';
  };

  const formatExperience = (exp?: string) => {
    const v = String(exp || '').trim();
    if (!v) return 'Mid Level';
    const hyphen = v.match(/^\d+\s*-\s*\d+$/);
    if (hyphen) return `${v.replace(/\s+/g, '')} years`;
    const fromLabel = getExperienceYearsLabel(v);
    return fromLabel || v;
  };

  const daysFromNow = (deadline?: string) => {
    if (!deadline) return '';
    const d = new Date(deadline);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diff = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'Expired';
    return `${diff} days from now`;
  };

  const isExpired = (j: any) => {
    const raw = j?.expiryDate || j?.expiry_date || j?.deadline;
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;
    return d.getTime() <= Date.now();
  };

  const extractCity = (val: any): string => {
    const raw = String(val || '').trim();
    if (!raw) return '';
    if (raw.includes(' - ')) {
      const after = raw.split(' - ')[1] || raw;
      const city = after.split(',')[0].trim();
      return city;
    }
    const toks = raw.split(/[,-]/).map(s => s.trim()).filter(Boolean);
    const skipWords = ['nepal', 'province', 'bagmati', 'gandaki', 'koshi', 'lumbini', 'madhesh', 'sudurpashchim'];
    for (const t of toks) {
      const k = t.toLowerCase();
      if (!skipWords.some(w => k.includes(w))) return t;
    }
    return toks[0] || raw;
  };

  return (
    <Card
      className={`relative cursor-pointer border ${tierInfo.border} bg-white rounded-xl shadow-sm min-h-[210px] sm:min-h-[220px] h-auto flex flex-col overflow-hidden gap-0`}
      onClick={() => onViewJob(job.id)}
    >

      {/* Tier indicator - hidden for 'latest' or unknown tiers, or if disabled via prop */}
      {showTierBadge && tierInfo.label && (
        <div className={`absolute top-2 right-2 ${tierColor} px-1.2 py-0.5 rounded-full flex items-center gap-1 z-20 shadow-md`}>
          <TierIcon className={`w-2 h-2 text-white`} />
          <span className="font-bold text-white text-[8px] sm:text-[9px]">{tierInfo.label}</span>
        </div>
      )}

      <CardContent className="p-0 !pb-0 flex flex-col flex-grow overflow-hidden">
        <div className="p-2 pb-0 flex flex-col flex-grow overflow-hidden">
          <div className="flex items-start gap-1 mb-0.5">
            {/* Logo Logic */}
            {(() => {
              const logo = imageMode === 'job' ? getJobCover(job) : getCompanyLogo(job);
              if (!logo) return null;
              return (
                <div className="relative flex-shrink-0">
                  <ImageWithFallback
                    src={logo}
                    alt={job.company || job.title}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded object-cover border border-gray-100"
                    key={`${job.id}-${imageUpdateTrigger}`}
                  />
                  {job.featured && (
                    <div className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-400 rounded-full flex items-center justify-center shadow-md z-20">
                      <Star className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-white fill-current" />
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <h4 className="font-bold text-gray-900 text-[11px] sm:text-[12px] leading-tight mb-0.5 line-clamp-2 break-words">
                {job.title}
              </h4>
              <div className="flex items-center space-x-1 min-w-0">
                <div className="truncate flex-1 min-w-0">
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <Link
                        to={`/company/${encodeURIComponent(job.company)}`}
                        onClick={(e) => { e.stopPropagation(); }}
                        className="inline-block text-[10px] sm:text-[11px] leading-tight font-medium text-[#1d73bb] hover:text-[#175f98] hover:underline underline-offset-2 truncate w-full p-0 m-0"
                      >
                        {job.company}
                      </Link>
                    </HoverCardTrigger>
                    {/* Hover Card Content remains same */}
                    {allCompanyJobs && allCompanyJobs.length > 1 && (
                      <HoverCardContent className="w-44 p-0 shadow-2xl border-blue-100 overflow-hidden rounded-xl z-[9999] max-w-full" align="center" side="bottom" sideOffset={5}>
                        <div className="bg-blue-600 p-1">
                          <h5 className="text-white font-bold text-[7px] flex items-center gap-1">
                            <Briefcase className="w-1.5 h-1.5" />
                            Jobs at {job.company}
                          </h5>
                        </div>
                        <div className="max-h-32 overflow-y-auto bg-white">
                          {allCompanyJobs.map((cJob) => (
                            <div
                              key={cJob.id}
                              className="p-1 hover:bg-blue-50 border-b border-gray-100 last:border-0 cursor-pointer transition-colors group/jobitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewJob(cJob.id);
                              }}
                            >
                              <div className="flex justify-between items-start gap-1">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 text-[7px] line-clamp-1 group-hover/jobitem:text-blue-600">
                                    {cJob.title}
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <span className="text-[6px] text-gray-500 flex items-center gap-0.5">
                                      <MapPin className="w-1.5 h-1.5" />
                                      {extractCity(cJob.location)}
                                    </span>
                                  </div>
                                </div>
                                <ChevronRight className="w-1.5 h-1.5 text-gray-300 group-hover/jobitem:translate-x-0.5 group-hover/jobitem:text-blue-500 transition-all" />
                              </div>
                            </div>
                          ))}
                        </div>
                        <div
                          className="p-1 bg-gray-50 text-center border-t border-gray-100 cursor-pointer hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `/company/${encodeURIComponent(job.company)}`;
                          }}
                        >
                          <span className="text-[6px] font-bold text-blue-600">View All Jobs</span>
                        </div>
                      </HoverCardContent>
                    )}
                  </HoverCard>
                </div>
                {companyJobCount > 1 && (
                  <Link
                    to={`/company/${encodeURIComponent(job.company)}`}
                    onClick={(e) => { e.stopPropagation(); }}
                    className="inline-block flex-shrink-0 ml-1 text-[10px] sm:text-[11px] leading-tight text-[#F0B6A5] hover:text-[#e09a85] font-semibold p-0 m-0"
                  >
                    {`${companyJobCount - 1}+`}
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1 mb-1 mt-1 flex-shrink-0">
            <div className="flex items-center text-[10px] sm:text-[11px] text-gray-700 gap-1 sm:gap-1.5 flex-wrap min-w-0 overflow-hidden">
              <span className="inline-flex items-center gap-1 whitespace-nowrap truncate max-w-[90px] sm:max-w-none">
                <Clock className="w-2.5 h-2.5 text-gray-400" />
                {formatExperience((job as any).experience)}
              </span>
              <span className="text-gray-300 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-1 truncate max-w-[90px] sm:max-w-[120px]">
                <MapPin className="w-2.5 h-2.5 text-gray-400" />
                {extractCity(job.location || '') || 'Remote'}
              </span>
            </div>
            {job.salary && (
              <div className="flex items-center text-[10px] sm:text-[11px] font-semibold text-green-600 gap-1 min-w-0">
                <TrendingUp className="w-2.5 h-2.5" />
                <span className="truncate">{job.salary}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 text-[9px] sm:text-[11px] mt-auto pt-1 mb-2 min-w-0 overflow-hidden">
            <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400 flex-shrink-0" />
            {isExpired(job) ? (
              <span className="text-red-500 font-semibold">Expired</span>
            ) : (
              <span className="text-gray-500 truncate flex-1 min-w-0">
                <span className="text-red-500 font-medium">Expires on:</span> {daysFromNow((job as any).deadline || (job as any).expiryDate || (job as any).expiry_date) || 'N/A'}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-0 flex-shrink-0 mt-auto pb-0">
          <div className="flex items-center justify-center gap-1 sm:gap-4 w-full px-1 mb-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 sm:h-7 sm:w-7 rounded-full hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-all"
              onClick={(e) => {
                e.stopPropagation();
                onViewJob(job.id);
              }}
              title="View details"
              aria-label="View details"
            >
              <Eye className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className={`h-6 w-6 sm:h-7 sm:w-7 rounded-full hover:bg-orange-50 transition-all ${isBookmarked ? 'text-orange-600' : 'text-gray-400 hover:text-orange-600'}`}
              onClick={handleSaveJob}
              title={isUserLoggedIn ? (isBookmarked ? "Remove from saved jobs" : "Save this job") : "Login to save jobs"}
              aria-label={isUserLoggedIn ? (isBookmarked ? 'Remove from saved jobs' : 'Save this job') : 'Login to save jobs'}
            >
              <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 sm:h-7 sm:w-7 rounded-full hover:bg-green-50 text-gray-400 hover:text-green-600 transition-all"
              onClick={handleShareJob}
              title="Share this job"
              aria-label="Share this job"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>

          <Button
            className={`w-full h-[30px] sm:h-[32px] text-[10px] sm:text-[11px] font-bold ${isApplied ? 'bg-gray-400 cursor-not-allowed' : (tierColor.includes('bg-') ? tierColor : `bg-[#1d73bb] ${tierColor}`)} text-white shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.01] rounded-t-md rounded-b-none`}
            onClick={handleApplyJob}
            disabled={isApplied}
          >
            {isApplied ? 'Applied' : 'Apply Now'}
          </Button>
        </div>
      </CardContent>

      {/* Share Job Modal - Only render when needed */}
      {showShareModal && (
        <SimpleShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          jobId={job.id}
          jobTitle={job.title}
        />
      )}
    </Card>
  );
});
